import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useTranslationHook from "../../../../hooks/useTranslationHook";
import { updateBuyerRegistrationField } from "../../../../../redux/slice/buyerSlice";
import { BUYER_REGISTRATION_STEPS } from "./constant";
import BidderRegistrationAgreementModal from "../modals/BidderRegistrationAgreementModal";
import { getLotFinancials } from "../../../../../utils";
import { formatNumberByLocale } from "../../../../../helpers";
import { handleSelection } from "../../../../../redux/slice/liveAuctionSlice";
import { handleAuctionBidRegistration } from "../../../../../redux/action/buyerAction";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

const BiddingLimit = () => {
    const dispatch = useDispatch();
    const lots = useSelector((state) => state.liveAuction.lots);
    const { t } = useTranslationHook();
    const location = useLocation();
    const { selectedLots, isBidderRegistrationModalOpen } = useSelector(
        (state) => state.buyer.buyerRegistration
    );
    const [nextBidAmount, setNextBidAmount] = useState(0);
    const registration_id = useSelector(
        (state) => state.buyer.buyerRegistration.registration_id
    );
    const liveAuction = useSelector((state) => state.liveAuction);
    // bid_increments
    const changeInterval = parseInt(1000);
    const data = lots?.data ?? [];
    const { selectedSessionID, lotID } = location?.state || {};
    
    const BidMultiplier = lots?.data?.[0]?.bid_limit_multiplier ?? 10;

    const { totalDeposit, biddingLimit, minRequiredDeposit, maxDepositAmount } =
        useMemo(() => {
            return getLotFinancials(data, selectedLots, BidMultiplier, {
                bidding_limit: liveAuction.bidding_limit,

                deposit_amount: liveAuction.deposit_amount,
            });
        }, [data, selectedLots]);

    let lang = useSelector((state) => state.translation.lang);

    const onBidChange = useCallback(
        (op) => {
            setNextBidAmount((prev) => {
                let next =
                    prev +
                    (op === "increase" ? changeInterval : -changeInterval);

                // derive deposit from next
                let deposit = Math.ceil(next / BidMultiplier);

                if (deposit < minRequiredDeposit) {
                    toast.dismiss("min_bid_toast");
                    toast.info(
                        t(
                            "You cannot go below the minimum bidding limit of {{amount}}",
                            {
                                amount: formatNumberByLocale(
                                    biddingLimit,
                                    lang
                                ),
                            }
                        ),
                        {
                            toastId: "min_bid_toast",
                        }
                    );

                    deposit = minRequiredDeposit;
                    next = deposit * BidMultiplier;
                }

                const maxDeposit = Math.floor(maxDepositAmount / BidMultiplier);

                if (deposit > maxDeposit) {
                    toast.dismiss("max_bid_toast");
                    toast.info(
                        t(
                            "You cannot exceed the maximum bidding limit of {{amount}}",
                            {
                                amount: formatNumberByLocale(
                                    maxDepositAmount,
                                    lang
                                ),
                            }
                        ),
                        {
                            toastId: "max_bid_toast",
                        }
                    );

                    deposit = maxDeposit;
                    next = deposit * BidMultiplier;
                }

                return next;
            });
        },
        [
            minRequiredDeposit,
            maxDepositAmount,
            changeInterval,
            t,
            lang,
            BidMultiplier,
        ]
    );
    // const depositRequired = useMemo(() => {
    //     return Math.ceil(nextBidAmount / BidMultiplier);
    // }, [nextBidAmount]);

    const depositRequired = useMemo(() => {
        const calculated = Math.ceil(nextBidAmount / BidMultiplier);

        return Math.min(
            Math.max(calculated, minRequiredDeposit),
            Math.floor(maxDepositAmount / BidMultiplier)
        );
    }, [nextBidAmount, minRequiredDeposit, maxDepositAmount]);

    const setBuyerField = useCallback((field, value) => {
        dispatch(updateBuyerRegistrationField({ field, value }));
    }, []);

    useEffect(() => {
        setNextBidAmount(biddingLimit);
    }, [biddingLimit]);

    const maxBid = useMemo(() => {
        return (nextBidAmount || 0) * 10;
    }, [nextBidAmount]);

    const goToNextStep = () => {
        setBuyerField("isBidderRegistrationModalOpen", true);
    };

    const onNextBtnClick = async () => {
        const payload = {
            registration_id: registration_id,
            auction_session: selectedSessionID,
            lots: selectedLots,
            deposit_amount: depositRequired,
            bid_limit: nextBidAmount,
            step: 3,
        };

        dispatch(
            handleSelection({
                deposit_amount: depositRequired,
                bidding_limit: nextBidAmount,
            })
        );
        const response = await dispatch(handleAuctionBidRegistration(payload));
        const { code, error, msg, data } = response || {};
        if (code !== 1 || error !== 0) {
            toast.info(msg || "Something went wrong");
            // console.info("Registration Detail Error:", response);
            return;
        }
        toast.success(t(msg));

        goToNextStep();
    };

    return (
        <div className="step-3">
            <section className="lot-progress">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8 col-md-10 col-sm-12">
                            <h5>
                                <span>
                                    {t("STEP 3/4")} <circle></circle>{" "}
                                    {t("Registering for Live Auction Name")}
                                </span>
                                {t("Bidding Limit")}
                                <em>
                                    {t(
                                        "Adjust your bidding limit to increase your bidding power."
                                    )}
                                </em>
                            </h5>
                            <ul>
                                <li className="step active"></li>
                                <li className="step active"></li>
                                <li className="step"></li>
                                <li></li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="lot-form">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8 col-md-10 col-sm-12">
                            <div className="register-for-someone">
                                <div className="chosse-price">
                                    <div className="block">
                                        <div className="price-calculator-box">
                                            <button
                                                className="circle"
                                                // disabled={nextBidAmount <= minRequiredDeposit}
                                                onClick={() =>
                                                    onBidChange("decrease")
                                                }
                                            >
                                                <img
                                                    src="/img/minus.svg"
                                                    alt=""
                                                />
                                            </button>
                                            <h3>
                                                <span>
                                                    {t("YOUR BIDDING LIMIT")}
                                                </span>
                                                <em className="dirham">D</em>{" "}
                                                {t("Amount", {
                                                    amount: formatNumberByLocale(
                                                        nextBidAmount,
                                                        lang
                                                    ),
                                                })}
                                            </h3>
                                            <button
                                                className="circle"
                                                // disabled={nextBidAmount >= maxDepositAmount}
                                                onClick={() =>
                                                    onBidChange("increase")
                                                }
                                            >
                                                <img
                                                    src="/img/plus-icon.svg"
                                                    alt=""
                                                />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="block">
                                        <p className="mb-2">
                                            {t("Deposit required")} &nbsp;
                                            <strong>
                                                <em className="dirham">D</em>{" "}
                                                {t("Amount", {
                                                    amount: formatNumberByLocale(
                                                        depositRequired,
                                                        lang
                                                    ),
                                                })}
                                            </strong>
                                        </p>
                                        {!selectedLots.length ? (
                                            <></>
                                        ) : (
                                            <p className="mb0">
                                                {t("Minimum Deposit required")}
                                                &nbsp;{" "}
                                                <strong>
                                                    <em className="dirham">
                                                        D
                                                    </em>{" "}
                                                    {t("Amount", {
                                                        amount: formatNumberByLocale(
                                                            minRequiredDeposit,
                                                            lang
                                                        ),
                                                    })}
                                                </strong>
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="bidding-rule">
                                    <h6>
                                        <img src="/img/info-icon.svg" alt="" />{" "}
                                        {t("Bidding Rules")}
                                    </h6>

                                    <ul>
                                        <li>
                                            {t(
                                                "You can bid on multiple properties, as long as your total active bids stay within your limit."
                                            )}
                                        </li>
                                        <li>
                                            {t(
                                                "Your deposit sets your bidding limit. The more you deposit, the more bidding power you have"
                                            )}
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="bidder-btns mt-4">
                                <button
                                    type="button"
                                    className="btn btn-sky back-btn"
                                    onClick={() => {
                                        setBuyerField(
                                            "step",
                                            BUYER_REGISTRATION_STEPS.LOTS_SELECTION
                                        );
                                    }}
                                >
                                    <img
                                        src="/img/arrow-left.svg"
                                        alt=""
                                        className="mr4"
                                        style={{
                                            transform:
                                                lang === "ar"
                                                    ? "rotate(180deg)"
                                                    : "none",
                                        }}
                                    />
                                    {t("Back")}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary forward-btn"
                                    // onClick={() => setBuyerField('isBidderRegistrationModalOpen', true)}>
                                    // onClick={() => setBuyerField('isBidderRegistrationModalOpen', true)}
                                    onClick={onNextBtnClick}
                                >
                                    {t("Next")}
                                    <img
                                        src="/img/arrow-right.svg"
                                        alt=""
                                        className="ml4"
                                        style={{
                                            transform:
                                                lang === "ar"
                                                    ? "rotate(180deg)"
                                                    : "none",
                                        }}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {isBidderRegistrationModalOpen && (
                <BidderRegistrationAgreementModal
                    show={isBidderRegistrationModalOpen}
                    onClose={() =>
                        setBuyerField("isBidderRegistrationModalOpen", false)
                    }
                    proceedToPaymentStep={() => {
                        setBuyerField("step", BUYER_REGISTRATION_STEPS.PAYMENT);
                        setBuyerField("isBidderRegistrationModalOpen", false);
                    }}
                />
            )}
        </div>
    );
};

export default BiddingLimit;
